import { createRouter, createWebHistory } from 'vue-router';

import { ApiClientError } from '@/lib/api-client';
import { currentUserQueryKey, fetchCurrentUser } from '@/lib/auth';
import { queryClient } from '@/lib/query-client';
import type { QueryQueueKey } from '@/lib/query-list';

const HomeView = () => import('@/views/HomeView.vue');
const LoginView = () => import('@/views/LoginView.vue');
const OrganizationAdminView = () => import('@/views/OrganizationAdminView.vue');
const PermissionAdminView = () => import('@/views/PermissionAdminView.vue');
const PermissionDeniedView = () => import('@/views/PermissionDeniedView.vue');
const RoleAdminView = () => import('@/views/RoleAdminView.vue');
const SystemView = () => import('@/views/SystemView.vue');
const UserAdminView = () => import('@/views/UserAdminView.vue');
const AgentAdminView = () => import('@/views/AgentAdminView.vue');
const QueryCreateView = () => import('@/views/QueryCreateView.vue');
const QueryListView = () => import('@/views/QueryListView.vue');
const QueryWorkspaceView = () => import('@/views/QueryWorkspaceView.vue');
const QueryIntakeListView = () => import('@/views/QueryIntakeListView.vue');
const QueryIntakeDetailView = () => import('@/views/QueryIntakeDetailView.vue');
const InboxView = () => import('@/views/InboxView.vue');
const MailboxAdminView = () => import('@/views/MailboxAdminView.vue');
const EmailTemplateAdminView = () => import('@/views/EmailTemplateAdminView.vue');
const SalesTerritoryAdminView = () =>
  import('@/views/SalesTerritoryAdminView.vue');
const InCountryLocationAdminView = () =>
  import('@/views/InCountryLocationAdminView.vue');
const HotelAdminView = () => import('@/views/HotelAdminView.vue');
const SupplierAdminView = () => import('@/views/SupplierAdminView.vue');
const ServiceAdminView = () => import('@/views/ServiceAdminView.vue');
const MarkupRuleAdminView = () => import('@/views/MarkupRuleAdminView.vue');
const HotelRateImportView = () => import('@/views/HotelRateImportView.vue');
const HotelRateCardAdminView = () => import('@/views/HotelRateCardAdminView.vue');
const CommercialTaxRuleAdminView = () =>
  import('@/views/CommercialTaxRuleAdminView.vue');

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    permission?: string;
    queuePermissions?: Partial<Record<QueryQueueKey, readonly string[]>>;
  }
}

const queryQueuePermissions = {
  PENDING_EMAILS: ['query.view', 'email.view'],
  PENDING_ACKNOWLEDGEMENT: ['query.view'],
  LIVE: ['query.view'],
  COLD: ['query.view'],
  PENDING_AVAILABILITY: ['query.view', 'availability.view'],
  READY_TO_INVOICE: ['query.view', 'availability.view'],
  PENDING_BOOKINGS: ['query.view'],
} as const satisfies Record<QueryQueueKey, readonly string[]>;

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/login', name: 'login', component: LoginView },
    { path: '/', name: 'home', component: HomeView, meta: { requiresAuth: true } },
    {
      path: '/system',
      name: 'system',
      component: SystemView,
      meta: { requiresAuth: true },
    },
    {
      path: '/admin/organization',
      name: 'organization-admin',
      component: OrganizationAdminView,
      meta: { requiresAuth: true, permission: 'organization.view' },
    },
    {
      path: '/admin/users',
      name: 'user-admin',
      component: UserAdminView,
      meta: { requiresAuth: true, permission: 'user.view' },
    },
    {
      path: '/admin/roles',
      name: 'role-admin',
      component: RoleAdminView,
      meta: { requiresAuth: true, permission: 'role.view' },
    },
    {
      path: '/admin/permissions',
      name: 'permission-admin',
      component: PermissionAdminView,
      meta: { requiresAuth: true, permission: 'permission.view' },
    },
    {
      path: '/agents',
      name: 'agent-admin',
      component: AgentAdminView,
      meta: { requiresAuth: true, permission: 'agent.view' },
    },
    {
      path: '/queries',
      name: 'query-list',
      component: QueryListView,
      meta: {
        requiresAuth: true,
        permission: 'query.view',
        queuePermissions: queryQueuePermissions,
      },
    },
    {
      path: '/queries/new',
      name: 'query-create',
      component: QueryCreateView,
      meta: { requiresAuth: true, permission: 'query.create' },
    },
    {
      path: '/queries/intake',
      name: 'query-intake-list',
      component: QueryIntakeListView,
      meta: { requiresAuth: true, permission: 'query_intake.view' },
    },
    {
      path: '/queries/intake/:intakeId',
      name: 'query-intake-detail',
      component: QueryIntakeDetailView,
      meta: { requiresAuth: true, permission: 'query_intake.view' },
    },
    {
      path: '/queries/inbox',
      name: 'query-inbox',
      component: InboxView,
      meta: { requiresAuth: true, permission: 'email.view' },
    },
    {
      path: '/queries/:queryId',
      name: 'query-workspace',
      component: QueryWorkspaceView,
      meta: { requiresAuth: true, permission: 'query.view' },
    },
    {
      path: '/administration/sales-territories',
      name: 'sales-territory-admin',
      component: SalesTerritoryAdminView,
      meta: { requiresAuth: true, permission: 'sales_territory.manage' },
    },
    {
      path: '/administration/mailboxes',
      name: 'mailbox-admin',
      component: MailboxAdminView,
      meta: { requiresAuth: true, permission: 'mailbox.manage' },
    },
    {
      path: '/administration/email-templates',
      name: 'email-template-admin',
      component: EmailTemplateAdminView,
      meta: { requiresAuth: true, permission: 'email_template.manage' },
    },
    {
      path: '/administration/commercial-tax-rules',
      name: 'commercial-tax-rule-admin',
      component: CommercialTaxRuleAdminView,
      meta: {
        requiresAuth: true,
        permission: 'commercial_tax_rule.view',
      },
    },
    {
      path: '/administration/quotation-tax-rules',
      redirect: '/administration/commercial-tax-rules',
    },
    {
      path: '/masters/in-country-locations',
      name: 'in-country-location-admin',
      component: InCountryLocationAdminView,
      meta: { requiresAuth: true, permission: 'in_country_location.view' },
    },
    {
      path: '/masters/hotels',
      name: 'hotel-admin',
      component: HotelAdminView,
      meta: { requiresAuth: true, permission: 'hotel.view' },
    },
    {
      path: '/masters/suppliers',
      name: 'supplier-admin',
      component: SupplierAdminView,
      meta: { requiresAuth: true, permission: 'supplier.view' },
    },
    {
      path: '/masters/markup-rules',
      name: 'markup-rule-admin',
      component: MarkupRuleAdminView,
      meta: { requiresAuth: true, permission: 'markup_rule.view' },
    },
    {
      path: '/masters/rate-imports',
      name: 'hotel-rate-imports',
      component: HotelRateImportView,
      meta: { requiresAuth: true, permission: 'rate_card.import' },
    },
    {
      path: '/masters/services',
      name: 'service-admin',
      component: ServiceAdminView,
      meta: { requiresAuth: true, permission: 'service.view' },
    },
    {
      path: '/admin/sales-territories',
      redirect: '/administration/sales-territories',
    },
    {
      path: '/permission-denied',
      name: 'permission-denied',
      component: PermissionDeniedView,
      meta: { requiresAuth: true },
    },
    {
      path: '/masters/hotel-rate-cards',
      name: 'hotel-rate-card-admin',
      component: HotelRateCardAdminView,
      meta: {
        requiresAuth: true,
        permission: 'rate_card.view',
      },
    },
  ],
});

router.beforeEach(async (to) => {
  if (to.meta.requiresAuth !== true) return true;

  try {
    const user = await queryClient.fetchQuery({
      queryKey: currentUserQueryKey,
      queryFn: fetchCurrentUser,
      retry: false,
      staleTime: 0,
    });
    const requiredPermissions = new Set<string>();
    if (to.meta.permission !== undefined) {
      requiredPermissions.add(to.meta.permission);
    }
    const queue = typeof to.query.queue === 'string' ? to.query.queue : undefined;
    if (queue !== undefined) {
      for (const permission of to.meta.queuePermissions?.[
        queue as QueryQueueKey
      ] ?? []) {
        requiredPermissions.add(permission);
      }
    }
    if (
      [...requiredPermissions].some(
        (permission) => !user.permissions.includes(permission),
      )
    ) {
      return { name: 'permission-denied' };
    }
    return true;
  } catch (error: unknown) {
    if (error instanceof ApiClientError && error.status === 401) {
      return { name: 'login', query: { redirect: to.fullPath } };
    }
    throw error;
  }
});
